import { ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from './firebase';

/**
 * Gets a download URL for a file in Firebase Storage
 * @param path The path to the file in Firebase Storage
 * @returns Promise with the download URL
 */
export const getFileDownloadURL = async (path: string): Promise<string> => {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    // Create a reference to the file
    const fileRef = ref(storage, path);
    
    // Get download URL
    const url = await getDownloadURL(fileRef);
    return url;
  } catch (error) {
    console.error('Error getting download URL:', error);
    throw error;
  }
};

/**
 * Uploads a profile photo to Firebase Storage and returns the download URL
 * 
 * @param dataUrl The data URL of the image
 * @returns The download URL of the uploaded image
 */
export const uploadProfilePhoto = async (dataUrl: string): Promise<string> => {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    // Generate a unique path for the profile photo
    const userId = auth.currentUser.uid;
    const timestamp = new Date().getTime();
    const path = `users/${userId}/profile/profile_${timestamp}.jpg`;
    
    // Create a reference to the file
    const storageRef = ref(storage, path);
    
    // Upload the data URL
    await uploadString(storageRef, dataUrl, 'data_url');
    
    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw error;
  }
};

/**
 * Deletes a file from Firebase Storage
 * 
 * @param url The URL of the file to delete
 */
export const deleteFileByUrl = async (url: string): Promise<void> => {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    // Extract path from URL - this is a simple approach that may need to be refined
    // based on how your storage URLs are structured
    const path = url.split('o/')[1]?.split('?')[0];
    
    if (!path) {
      throw new Error('Invalid file URL');
    }
    
    // Decode the path (Firebase URLs are URL encoded)
    const decodedPath = decodeURIComponent(path);
    
    // Create a reference to the file
    const storageRef = ref(storage, decodedPath);
    
    // Delete the file
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Downloads a file from Firebase Storage
 * @param path The path to the file in Firebase Storage
 * @param filename Optional filename to use for the download
 */
export const downloadFile = async (path: string, filename?: string): Promise<void> => {
  try {
    const url = await getFileDownloadURL(path);
    
    // Create anchor element to download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || path.split('/').pop() || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Checks if a user has access to a particular file in storage
 * @param path The path to check
 * @returns Promise resolving to true if accessible
 */
export const checkFileAccess = async (path: string): Promise<boolean> => {
  try {
    await getFileDownloadURL(path);
    return true;
  } catch (error) {
    return false;
  }
};

// Export a storage service object
export const storageService = {
  getFileDownloadURL,
  downloadFile,
  checkFileAccess,
  uploadProfilePhoto,
  deleteFileByUrl
}; 